'use client';

import { DATA_GRID_MODE } from '@/constants/dataGrid';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  FormControlLabel,
} from '@/primitives';
import { alpha } from '@mui/material/styles';
import {
  CircularProgress,
  styled,
  SwitchProps,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { Pagination } from '@/primitives/Pagination';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { AnimalListRow } from '@/hooks/publicSearch/animal/helpers';
import {
  GridLogicOperator,
  GridPreferencePanelsValue,
} from '@mui/x-data-grid-pro';
import type {
  GridCallbackDetails,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridPaginationModel,
  GridRowIdGetter,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import { debounce } from 'lodash';
import { QUICK_SEARCH_DEBOUNCE_MS } from '../search/quickSearchConfig';
import { buildQuickSearchFilterFromTerm } from '../search/quickSearchFilterBuilder';
import { useAnimalQuickSearchDefaultOperator } from '@/hooks/publicSearch/useAnimalQuickSearchDefaultOperator';
import { format } from 'date-fns';
import PublicSearchDataGrid, {
  searchApiRef,
} from '@/components/publicSearch/searchSection/PublicSearchDataGrid';
import { useAnimalProgenyEbvFilter } from '@/hooks/publicSearch/animal/useAnimalProgenyEbvFilter';
import { getVisibleFieldKeysFromAnimalSearchConfig } from '@/hooks/publicSearch/animal/animalEbvGrid';
import { useIsEbvEnabled } from '@/hooks/publicSearch/useIsEbvEnabled';
import { useEbvDisplayOptions } from '@/hooks/publicSearch/useEbvDisplayOptions';
import BreedplanInterpretationInfoTrigger from '@/components/publicSearch/animal/ebv/BreedplanInterpretationInfoTrigger';
import {
  formatEbvAccuracyLabel,
  formatEbvPluralLabel,
} from '@/components/publicSearch/animal/ebv/ebvProviderCapabilities';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import useGridStore from '@/publicSearch/store/useGridStore';
import {
  AnimalDefaultFieldOrder,
  FieldOption,
  FilterInput,
  GetAnimalsRow,
  LogicalOperator,
} from '@/components/publicSearch/constants';
import { useRouter } from 'next/navigation';
import { ROUTE_SEGMENT } from '@/constants/routeSegments';
import { black, slate } from '@/lib/colours';
import { theme } from '@/lib/theme';
import { quickSearchInputSx } from '@/components/publicSearch/search/constants';
import { StyledSearchIconButton } from '@/components/publicSearch/searchPage/SearchActions';
import { ISO_DATE_FORMAT } from '@/configs';
import {
  ANIMAL_TAB_BAR_HEIGHT,
  FOOTER_HEIGHT,
  NAV_HEIGHT,
} from '@/configs/layout';
import {
  getEbvIndexFields,
  parseEbvConfig,
} from '@/hooks/publicSearch/ebv/ebvColumns';

type ProgenyTableProps = {
  animalId: string;
  orgId: string;
  /** Height in px of the space left for this panel under the host's tab bar.
   *  The host measures it, so the panel doesn't have to re-derive the page
   *  chrome. Ignored below `md`, where a card list renders instead. */
  panelHeight?: number;
};

/** Stand-in until the host's measurement lands — the shell chrome bounding the
 *  page scroller (`Shell` gives it `100vh` less the nav and the in-flow
 *  footer) plus the tab bar. Close but not exact: it can't see the public
 *  search layout's few px of top padding. In practice the host measures during
 *  the same commit that mounts this, so it rarely reaches paint. */
const PROGENY_PANEL_FALLBACK_HEIGHT = `calc(100vh - ${
  NAV_HEIGHT + FOOTER_HEIGHT + ANIMAL_TAB_BAR_HEIGHT
}px)`;

/** Floor so the grid stays usable on short viewports (landscape tablets,
 *  split-screen windows) where the remaining space would otherwise leave room
 *  for only a row or two. */
const PROGENY_PANEL_MIN_HEIGHT = 400;

const DisplayIOSSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 40,
  height: 24,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: '2px',
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: theme.palette.primary.contrastText,
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.primary.main,
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: theme.palette.primary.main,
      border: `4px solid ${theme.palette.common.white}`,
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.grey[100],
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.7,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: '18px',
    height: '18px',
  },
  '& .MuiSwitch-track': {
    borderRadius: '56px',
    backgroundColor: slate[400],
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));

// Quick-search input shared by the mobile and desktop progeny views. Only the
// outer `sx` differs between the two layouts; everything else is identical.
function ProgenySearchField({
  value,
  onChange,
  sx,
  compact = false,
}: {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  sx: SxProps<Theme>;
  /** Phone layout: the long placeholder does not fit the field and truncates. */
  compact?: boolean;
}) {
  return (
    <TextField
      fullWidth
      size="small"
      placeholder={
        compact ? 'Name or ident' : 'Search by animal name or identifier'
      }
      value={value}
      slotProps={{
        input: {
          sx: { px: 0.5 },
          startAdornment: (
            <IconButton
              size="small"
              tabIndex={-1}
              disableRipple
              aria-hidden
              onMouseDown={(event) => event.preventDefault()}
              sx={{
                pointerEvents: 'none',
                cursor: 'default',
                '& svg': { fontSize: '1.125rem' },
              }}
            >
              <TravelExploreIcon />
            </IconButton>
          ),
        },
      }}
      onChange={onChange}
      sx={sx}
    />
  );
}

export default function ProgenyTable({
  animalId,
  orgId,
  panelHeight,
}: ProgenyTableProps) {
  const router = useRouter();
  const { breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.down('md'));
  const orgCode = usePublicSearchContext(
    (context) => context.society.societyCode!,
  );
  const societySettings = usePublicSearchContext(
    (context) => context.society.settings,
  );
  const isEbvEnabled = useIsEbvEnabled();
  const { ebvLabel, showPercentileSearch, showBreedplanHelp } =
    useEbvDisplayOptions();
  const ebvPluralLabel = formatEbvPluralLabel(ebvLabel);
  const ebvAccuracyLabel = formatEbvAccuracyLabel(ebvLabel);

  // Default visible columns come from the society's ANIMAL_LIST_FIELDS config
  // (falling back to the generic default order) so the progeny grid matches
  // the society-specific columns of the main animal search.
  const animalSearchVisibleFieldKeys = useMemo(
    () => getVisibleFieldKeysFromAnimalSearchConfig(societySettings),
    [societySettings],
  );
  const {
    showEBVs,
    showEBVAccuracy,
    showPercentiles,
    setShowEBVs,
    setShowEBVAccuracy,
    setShowPercentiles,
  } = useGridStore();
  const ebvIndexFields = useMemo(
    () => getEbvIndexFields(parseEbvConfig(societySettings)),
    [societySettings],
  );

  useEffect(() => {
    if (!isEbvEnabled) {
      setShowEBVs(false);
      setShowEBVAccuracy(false);
      setShowPercentiles(false);
    }
  }, [isEbvEnabled, setShowEBVs, setShowEBVAccuracy, setShowPercentiles]);

  // Clear persisted percentile toggle when society profile disables percentile search (e.g. CANG).
  useEffect(() => {
    if (!showPercentileSearch) {
      setShowPercentiles(false);
    }
  }, [showPercentileSearch, setShowPercentiles]);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  const [gridSortModel, setGridSortModel] = useState<GridSortModel>([]);
  const [gridFilterModel, setGridFilterModel] = useState<GridFilterModel>({
    items: [],
    logicOperator: GridLogicOperator.And,
  });
  const [filters, setFilters] = useState<FilterInput[]>([]);
  const [commonFilterOperator, setCommonFilterOperator] =
    useState<LogicalOperator>(LogicalOperator.and);
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});
  const [manualColumnView, setManualColumnView] = useState<boolean>(false);
  const effectiveColumnVisibilityModel = useMemo(() => {
    if (showEBVs) return columnVisibilityModel;
    const next = { ...columnVisibilityModel };
    ebvIndexFields.forEach((field) => {
      next[field] = false;
    });
    return next;
  }, [columnVisibilityModel, ebvIndexFields, showEBVs]);

  const [identifierInput, setIdentifierInput] = useState<string>('');
  const [debouncedIdentifier, setDebouncedIdentifier] = useState<string>('');
  const prevDebouncedIdent = useRef<string | undefined>(undefined);
  const quickSearchDefaultOperator = useAnimalQuickSearchDefaultOperator();

  const [displayMenuAnchor, setDisplayMenuAnchor] =
    useState<null | HTMLElement>(null);
  const displayMenuOpen = Boolean(displayMenuAnchor);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedIdentifier(identifierInput);
    }, QUICK_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [identifierInput]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- resets the quick-search input when the viewed animal changes (reset-on-prop)
    setIdentifierInput('');
  }, [animalId]);

  useEffect(() => {
    if (prevDebouncedIdent.current === undefined) {
      prevDebouncedIdent.current = debouncedIdentifier;
      return;
    }
    if (prevDebouncedIdent.current !== debouncedIdentifier) {
      prevDebouncedIdent.current = debouncedIdentifier;
      // eslint-disable-next-line react-hooks/set-state-in-effect -- resets to the first page only when the debounced search term actually changes (tracked via ref), not on every render
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }
  }, [debouncedIdentifier]);

  const queryFilters = useMemo(() => {
    const quickSearchFilter = buildQuickSearchFilterFromTerm(
      debouncedIdentifier,
      FieldOption.searchTerm.value,
      quickSearchDefaultOperator,
    );
    return quickSearchFilter ? [...filters, quickSearchFilter] : filters;
  }, [debouncedIdentifier, filters, quickSearchDefaultOperator]);

  const handleAverageRowClick = useCallback(() => {
    if (!showPercentileSearch) return;
    router.push(`/${orgCode}/${ROUTE_SEGMENT.ANIMALS}/ebv-percentages`);
  }, [orgCode, router, showPercentileSearch]);

  const progenyQuery = useAnimalProgenyEbvFilter({
    animalId,
    orgId,
    orgCode,
    enabled: Boolean(animalId && orgId),
    paginationModel,
    gridSortModel,
    filters: queryFilters,
    commonFilterOperator,
    showEBVs,
    showEBVAccuracy,
    showPercentiles,
    isCardView: false,
    onAverageRowClick: showPercentileSearch ? handleAverageRowClick : undefined,
  });

  const {
    data,
    dataGrid,
    loading,
    previousData,
    done: queryDone,
    ebvTraitExplanationPopover,
  } = progenyQuery;

  const handleFilterModelChange = useMemo(
    () =>
      debounce(
        (model: GridFilterModel, details: GridCallbackDetails<'filter'>) => {
          if (!details.reason) return;

          setFilters(
            model.items.map(({ field, operator, value }) => ({
              field,
              operator,
              value:
                value instanceof Date ? format(value, ISO_DATE_FORMAT) : value,
            })),
          );
          setCommonFilterOperator(
            model.logicOperator === GridLogicOperator.Or
              ? LogicalOperator.or
              : LogicalOperator.and,
          );
          setGridFilterModel(model);
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
        },
        QUICK_SEARCH_DEBOUNCE_MS,
      ),
    [],
  );

  const columnFields = useMemo(
    () => dataGrid?.columns?.map((col) => col.field).join(',') || '',
    [dataGrid?.columns],
  );

  const handleColumnVisibilityChange = (model: GridColumnVisibilityModel) => {
    const next = { ...model };
    if (!showEBVs) {
      ebvIndexFields.forEach((field) => {
        next[field] = false;
      });
    }
    setManualColumnView(true);
    setColumnVisibilityModel(next);
  };

  useEffect(() => {
    if (!dataGrid?.columns?.length || manualColumnView) return;

    const colViewModel: GridColumnVisibilityModel = {};
    dataGrid.columns.forEach((col) => {
      colViewModel[col.field] = false;
    });

    const animalFieldKeys =
      animalSearchVisibleFieldKeys.length > 0
        ? animalSearchVisibleFieldKeys
        : AnimalDefaultFieldOrder.map(({ value }) => value);
    animalFieldKeys.forEach((fieldKey) => {
      if (dataGrid.columns.some((col) => col.field === fieldKey)) {
        colViewModel[fieldKey] = true;
      }
    });

    const showTraitColumns =
      isEbvEnabled && (showEBVs || showEBVAccuracy || showPercentiles);
    if (showTraitColumns) {
      dataGrid.columns.forEach((col) => {
        if (col.field.startsWith('EBV')) {
          colViewModel[col.field] = ebvIndexFields.has(col.field)
            ? showEBVs
            : true;
        }
      });
    }

    const keys = Object.keys(colViewModel);
    const isSame =
      keys.length === Object.keys(columnVisibilityModel).length &&
      keys.every((key) => columnVisibilityModel[key] === colViewModel[key]);
    if (isSame) return;

    // eslint-disable-next-line react-hooks/set-state-in-effect -- derives default column visibility from async-loaded grid columns + society config; guarded against re-running with an equality check
    setColumnVisibilityModel(colViewModel);
  }, [
    columnFields,
    dataGrid?.columns,
    ebvIndexFields,
    manualColumnView,
    columnVisibilityModel,
    animalSearchVisibleFieldKeys,
    showEBVs,
    showEBVAccuracy,
    showPercentiles,
    isEbvEnabled,
  ]);

  const handleOpenDisplayMenu = (event: React.MouseEvent<HTMLElement>) => {
    setDisplayMenuAnchor(event.currentTarget);
  };

  const handleCloseDisplayMenu = () => {
    setDisplayMenuAnchor(null);
  };

  const handleManageColumns = () => {
    handleCloseDisplayMenu();
    searchApiRef.current?.showPreferences(GridPreferencePanelsValue.columns);
  };

  const handleResetProgenyLayout = () => {
    handleCloseDisplayMenu();
    setManualColumnView(false);
    setColumnVisibilityModel({});
  };

  useEffect(() => {
    if (!isEbvEnabled || (!showEBVs && !showEBVAccuracy && !showPercentiles))
      return;
    const rafId = window.requestAnimationFrame(() => {
      searchApiRef.current?.autosizeColumns?.({
        includeHeaders: true,
        includeOutliers: true,
        expand: true,
      });
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [isEbvEnabled, showEBVs, showEBVAccuracy, showPercentiles]);

  const rowCount = loading
    ? (previousData?.totalCount ?? 0)
    : (data?.totalCount ?? 0);
  const animalRows = (dataGrid?.rows ?? []) as GetAnimalsRow[];

  if (isMobile) {
    const hasNoProgeny = queryDone && !loading && animalRows.length === 0;
    const showControls = queryDone && !loading && animalRows.length > 0;
    return (
      <Box sx={{ width: '100%' }}>
        {showControls && (
          <ProgenySearchField
            compact
            value={identifierInput}
            onChange={(e) => setIdentifierInput(e.target.value)}
            sx={[
              quickSearchInputSx,
              {
                mb: 1.5,
                '& .MuiInputBase-root': {
                  '& fieldset': {
                    border: '1.5px solid',
                    borderColor: (th) => th.palette.primary.main,
                  },
                },
                '& input': { fontSize: '16px' }, // iOS Safari auto-zooms inputs with font-size < 16px and doesn't zoom back out
              },
            ]}
          />
        )}
        {(() => {
          if (loading) {
            return (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}
              >
                <CircularProgress size={16} />
                <Typography
                  variant="bodysmall"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Loading progeny…
                </Typography>
              </Box>
            );
          }
          if (hasNoProgeny) {
            return (
              <Typography
                variant="bodysmall"
                sx={{
                  color: 'text.secondary',
                }}
              >
                No progeny found.
              </Typography>
            );
          }
          return (
            <Box
              sx={{
                backgroundColor: 'background.paper',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              {animalRows.map((row, i) => (
                <AnimalListRow key={row.ID ?? i} orgCode={orgCode} row={row} />
              ))}
            </Box>
          );
        })()}
        {showControls && (
          <Box sx={{ mt: 2 }}>
            <Pagination
              page={paginationModel.page}
              pageCount={Math.ceil(rowCount / paginationModel.pageSize)}
              pageSize={paginationModel.pageSize}
              rowCount={rowCount}
              onPageChange={(page) =>
                setPaginationModel({ ...paginationModel, page })
              }
              onPageSizeChange={(pageSize) =>
                setPaginationModel({ page: 0, pageSize })
              }
            />
          </Box>
        )}
      </Box>
    );
  }

  const availableHeight =
    panelHeight === undefined
      ? PROGENY_PANEL_FALLBACK_HEIGHT
      : `${panelHeight}px`;

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflowX: 'hidden',
      }}
    >
      <Box
        sx={{
          // Takes exactly the space left under the tab bar, so scrolling the
          // info card away lands the panel's bottom edge on the viewport edge
          // with nothing further to scroll. The toolbar sits inside this box
          // and shares the height with the grid via flex, which is why the
          // host's measurement only covers the chrome above the panel.
          height: `max(${PROGENY_PANEL_MIN_HEIGHT}px, ${availableHeight})`,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <Stack
          direction="row"
          sx={{
            flexWrap: 'wrap',
            alignItems: 'center',
            flexShrink: 0,
            py: '8px',
            px: '10px',
            mb: 2,
            backgroundColor: 'background.paper',
            borderRadius: '8px',
            boxShadow: `0 2px 8px ${alpha(black[900], 0.08)}`,
            gap: '10px',
          }}
        >
          <ProgenySearchField
            value={identifierInput}
            onChange={(e) => setIdentifierInput(e.target.value)}
            sx={[
              quickSearchInputSx,
              {
                flex: 1,
                minWidth: 200,
                position: 'relative',
                height: '38px',
                width: '100%',
                textTransform: 'none',
                '& .MuiInputBase-root': {
                  '& fieldset': {
                    border: '1.5px solid',
                    borderColor: (th) => th.palette.primary.main,
                  },
                  '&:hover fieldset': {
                    borderColor: (th) => th.palette.primary.dark,
                  },
                  '&.Mui-focused fieldset': {
                    borderWidth: '2px',
                    borderColor: (th) => th.palette.primary.main,
                  },
                },
                '& input': {
                  padding: '8px 12px 8px 0px',
                  height: '20px',
                  fontSize: '16px', // iOS Safari auto-zooms inputs with font-size < 16px and doesn't zoom back out
                  '&:placeholder-shown': {
                    textOverflow: 'ellipsis',
                  },
                },
                fieldset: {
                  height: '44px',
                },
              },
            ]}
          />
          {isEbvEnabled && (
            <BreedplanInterpretationInfoTrigger
              show={showBreedplanHelp}
              dataDdActionName="Progeny Table: BREEDPLAN Interpretation Info"
            />
          )}
          <Tooltip title="Display options" placement="bottom">
            <StyledSearchIconButton
              aria-label="Display options"
              sx={{
                height: 36,
                width: 36,
                flexShrink: 0,
              }}
              onClick={handleOpenDisplayMenu}
            >
              <TuneOutlinedIcon color="primary" sx={{ fontSize: 20 }} />
            </StyledSearchIconButton>
          </Tooltip>
          <Menu
            anchorEl={displayMenuAnchor}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            open={displayMenuOpen}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            onClose={handleCloseDisplayMenu}
            slotProps={{
              list: {
                sx: {
                  width: 'min(calc(100vw - 48px), 280px)',
                  li: {
                    p: 0,
                    fontSize: '12px',
                    label: {
                      m: 0,
                      pl: '8px',
                      height: '36px',
                      width: '100%',
                      fontSize: '12px',
                    },
                    '& .MuiFormControlLabel-label': {
                      fontSize: '12px',
                    },
                    '.MuiSwitch-root': {
                      position: 'absolute',
                      right: '8px',
                      transform: 'scale(0.8)',
                      transformOrigin: 'top right',
                    },
                  },
                },
              },
            }}
          >
            {isEbvEnabled && (
              <MenuItem
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
              >
                <FormControlLabel
                  control={
                    <DisplayIOSSwitch
                      checked={showEBVs}
                      onChange={(e) => setShowEBVs(e.target.checked)}
                    />
                  }
                  label={ebvPluralLabel}
                />
              </MenuItem>
            )}
            {isEbvEnabled && (
              <MenuItem
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
              >
                <FormControlLabel
                  control={
                    <DisplayIOSSwitch
                      checked={showEBVAccuracy}
                      onChange={(e) => setShowEBVAccuracy(e.target.checked)}
                    />
                  }
                  label={ebvAccuracyLabel}
                />
              </MenuItem>
            )}
            {isEbvEnabled && showPercentileSearch && (
              <MenuItem
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
              >
                <FormControlLabel
                  control={
                    <DisplayIOSSwitch
                      checked={showPercentiles}
                      onChange={(e) => setShowPercentiles(e.target.checked)}
                    />
                  }
                  label="Percentiles"
                />
              </MenuItem>
            )}
            <MenuItem
              onClick={handleManageColumns}
              sx={{
                height: '36px',
                minHeight: '36px',
                py: 0,
                paddingLeft: '8px !important',
                display: 'flex',
                alignItems: 'center',
                fontSize: '12px',
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              Manage columns
            </MenuItem>
            <MenuItem
              onClick={handleResetProgenyLayout}
              sx={{
                height: '36px',
                minHeight: '36px',
                py: 0,
                paddingLeft: '8px !important',
                display: 'flex',
                alignItems: 'center',
                fontSize: '12px',
              }}
            >
              Reset layout
            </MenuItem>
          </Menu>
        </Stack>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <PublicSearchDataGrid
            columns={dataGrid?.columns ?? []}
            columnGroupingModel={dataGrid?.columnGroupingModel}
            columnVisibilityModel={effectiveColumnVisibilityModel}
            filterDebounceMs={QUICK_SEARCH_DEBOUNCE_MS}
            filterMode={DATA_GRID_MODE.SERVER}
            filterModel={gridFilterModel}
            // The hook types its getter against `GetAnimalsRow`; the grid hands
            // it the wider `GridValidRowModel`, so the parameter types don't
            // line up without widening it here.
            getRowId={dataGrid?.getRowId as GridRowIdGetter | undefined}
            initialState={{
              columns: {
                columnVisibilityModel: effectiveColumnVisibilityModel,
              },
            }}
            loading={loading}
            pagination
            paginationModel={paginationModel}
            rowCount={rowCount}
            rows={dataGrid?.rows ?? []}
            sortModel={gridSortModel}
            onColumnVisibilityModelChange={handleColumnVisibilityChange}
            onFilterModelChange={handleFilterModelChange}
            onPaginationModelChange={setPaginationModel}
            onSortModelChange={setGridSortModel}
          />
        </Box>
      </Box>
      {ebvTraitExplanationPopover}
    </Box>
  );
}
